import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GoogleAuthProvider } from './context/GoogleAuthContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';

// Lazy load components with prefetch
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Products = lazy(() => import('./components/Products'));
const Home = lazy(() => import('./components/Home'));
const Cart = lazy(() => import('./components/Cart'));
const Checkout = lazy(() => import('./components/Checkout'));
const OrderConfirmation = lazy(() => import('./components/OrderConfirmation'));
const Profile = lazy(() => import('./components/Profile'));
const Orders = lazy(() => import('./components/Orders'));
const Wishlist = lazy(() => import('./components/Wishlist'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Addresses = lazy(() => import('./components/Addresses'));
const Payments = lazy(() => import('./components/Payments'));
const Settings = lazy(() => import('./components/Settings'));
const Help = lazy(() => import('./components/Help'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));

// Route Constants
const ROUTES = {
  HOME: '/',
  PRODUCTS: '/products',
  CART: '/cart',
  LOGIN: '/login',
  REGISTER: '/register',
  RESET_PASSWORD: '/reset-password',
  FORGOT_PASSWORD: '/forgot-password',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  ORDERS: '/orders',
  WISHLIST: '/wishlist',
  ADDRESSES: '/addresses',
  PAYMENTS: '/payments',
  SETTINGS: '/settings',
  SECURITY: '/security',
  HELP: '/help',
  CHECKOUT: '/checkout',
  ORDER_CONFIRMATION: '/order-confirmation',
  AUTH_CALLBACK: '/auth/google/success',
};

// Optimized Loading Component with Skeleton
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)'
  }}>
    <div style={{
      textAlign: 'center',
      padding: '2rem'
    }}>
      <div style={{
        width: '60px',
        height: '60px',
        margin: '0 auto 1rem',
        border: '3px solid #E5E7EB',
        borderTopColor: '#2D5016',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <p style={{ color: '#6B7280' }}>Loading...</p>
    </div>
  </div>
);

// Role-based access constants
const USER_ROLES = {
  CUSTOMER: 'customer',
  FARMER: 'farmer',
  ADMIN: 'admin',
  DELIVERY: 'delivery',
};

// Optimized Protected Route with smooth transitions
const ProtectedRoute = ({ children, allowedRoles = null, redirectTo = ROUTES.LOGIN }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  if (loading || isRedirecting) {
    return <PageLoader />;
  }
  
  if (!isAuthenticated) {
    setIsRedirecting(true);
    return <Navigate to={redirectTo} replace />;
  }
  
  // Role-based access control
  if (allowedRoles && !allowedRoles.includes(user?.userType)) {
    setIsRedirecting(true);
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  
  return children;
};

// Optimized Public Route
const PublicRoute = ({ children, redirectTo = ROUTES.DASHBOARD }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  if (loading || isRedirecting) {
    return <PageLoader />;
  }
  
  if (isAuthenticated) {
    setIsRedirecting(true);
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
};

// Optional route guard
const OptionalRoute = ({ children }) => {
  const { loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  return children;
};

// Not Found Component
const NotFound = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #2D5016 0%, #5cb45c 100%)',
    padding: '2rem'
  }}>
    <div style={{
      background: 'rgba(255, 255, 255, 0.95)',
      padding: '3rem',
      borderRadius: '24px',
      textAlign: 'center',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      maxWidth: '500px',
      width: '100%'
    }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔍</div>
      <h1 style={{ color: '#2d3748', marginBottom: '1rem', fontSize: '2rem' }}>404</h1>
      <p style={{ color: '#718096', marginBottom: '2rem' }}>Page not found</p>
      <button 
        onClick={() => window.location.href = ROUTES.HOME}
        style={{
          padding: '0.75rem 1.5rem',
          background: 'linear-gradient(135deg, #2D5016 0%, #4A7C1F 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '10px',
          cursor: 'pointer'
        }}
      >
        Go Home
      </button>
    </div>
  </div>
);

// Security Settings Component
const SecuritySettings = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #2D5016 0%, #5cb45c 100%)',
    padding: '2rem'
  }}>
    <div style={{
      background: 'rgba(255, 255, 255, 0.95)',
      padding: '3rem',
      borderRadius: '24px',
      textAlign: 'center',
      maxWidth: '500px',
      width: '100%'
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛡️</div>
      <h2 style={{ color: '#2d3748', marginBottom: '1rem' }}>Security Settings</h2>
      <p style={{ color: '#718096', marginBottom: '2rem' }}>
        Manage your account security settings including password, two-factor authentication, and active sessions.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <button 
          onClick={() => window.location.href = ROUTES.PROFILE}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #2D5016 0%, #4A7C1F 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer'
          }}
        >
          Go to Profile Security
        </button>
        <button 
          onClick={() => window.location.href = ROUTES.HELP}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'white',
            color: '#2D5016',
            border: '2px solid #2D5016',
            borderRadius: '10px',
            cursor: 'pointer'
          }}
        >
          Get Help
        </button>
      </div>
    </div>
  </div>
);

// Prefetch component for faster navigation
const PrefetchLinks = () => {
  useEffect(() => {
    // Prefetch critical routes
    const links = ['/products', '/cart'];
    links.forEach(link => {
      const linkTag = document.createElement('link');
      linkTag.rel = 'prefetch';
      linkTag.href = link;
      document.head.appendChild(linkTag);
    });
  }, []);
  return null;
};

// Main App Component
function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isGoogleConfigured = googleClientId && googleClientId !== 'your-google-client-id-here';
  
  if (!isGoogleConfigured) {
    console.warn('⚠️ Google Client ID is not configured. Google login will not work.');
  }

  // App content with routes
  const AppContent = () => (
    <Router>
      <PrefetchLinks />
      <Suspense fallback={<PageLoader />}>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path={ROUTES.HOME} element={<Home />} />
            <Route path={ROUTES.PRODUCTS} element={<Products />} />
            <Route path={ROUTES.CART} element={<Cart />} />
            
            {/* Auth Routes */}
            <Route 
              path={ROUTES.LOGIN} 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            
            <Route 
              path={ROUTES.REGISTER} 
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              } 
            />
            
            {/* Password Reset Routes */}
            <Route 
              path={ROUTES.RESET_PASSWORD} 
              element={
                <OptionalRoute>
                  <ResetPassword />
                </OptionalRoute>
              } 
            />
            
            <Route 
              path={ROUTES.FORGOT_PASSWORD} 
              element={
                <OptionalRoute>
                  <ResetPassword />
                </OptionalRoute>
              } 
            />
            
            {/* Google OAuth Callback */}
            <Route 
              path={ROUTES.AUTH_CALLBACK} 
              element={
                <OptionalRoute>
                  <AuthCallback />
                </OptionalRoute>
              } 
            />
            
            {/* Protected Routes - Core Features */}
            <Route 
              path={ROUTES.DASHBOARD} 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.PROFILE} 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.ORDERS} 
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.WISHLIST} 
              element={
                <ProtectedRoute>
                  <Wishlist />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.ADDRESSES} 
              element={
                <ProtectedRoute>
                  <Addresses />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.PAYMENTS} 
              element={
                <ProtectedRoute>
                  <Payments />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.SETTINGS} 
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.SECURITY} 
              element={
                <ProtectedRoute>
                  <SecuritySettings />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.HELP} 
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              } 
            />
            
            {/* Checkout Routes */}
            <Route 
              path={ROUTES.CHECKOUT} 
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path={ROUTES.ORDER_CONFIRMATION} 
              element={
                <ProtectedRoute>
                  <OrderConfirmation />
                </ProtectedRoute>
              } 
            />
            
            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Suspense>
    </Router>
  );

  // If Google is not configured, render without Google OAuth
  if (!isGoogleConfigured) {
    return (
      <ErrorBoundary>
        <AuthProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  // Full app with Google OAuth
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider 
        clientId={googleClientId} 
        onScriptLoadError={() => console.error('Google OAuth script failed to load')}
      >
        <GoogleAuthProvider>
          <AuthProvider>
            <CartProvider>
              <AppContent />
            </CartProvider>
          </AuthProvider>
        </GoogleAuthProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
